
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BusinessType } from "@/types/payment";
import { supabase } from "@/lib/supabase";
import { Building, Building2, Users } from "lucide-react";

const businessTypeInfo = {
  small: {
    title: "Small Business (Startup)",
    icon: Building,
    description: "Perfect for startups and small teams (1-10 employees)",
    setupFee: 150,
    flutterwaveLink: "https://flutterwave.com/pay/8zbx459jbzbb",
  },
  medium: {
    title: "Mid-Sized Business", 
    icon: Building2,
    description: "Ideal for growing companies (11-100 employees)",
    setupFee: 200,
    flutterwaveLink: "https://flutterwave.com/pay/z0ubrblz7uif",
  },
  enterprise: {
    title: "Enterprise",
    icon: Users,
    description: "For large organizations (100+ employees)",
    setupFee: 250,
    flutterwaveLink: "https://flutterwave.com/pay/qv83cxn44krr",
  },
};

export const BusinessTypeSelect = () => {
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<BusinessType | null>(null);

  const handleSelectBusinessType = async (type: BusinessType) => {
    try {
      setLoading(true);
      setSelectedType(type);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Please sign in to continue");
        return;
      }

      const businessInfo = businessTypeInfo[type];
      
      // Create a payment record in the database for tracking
      const { error: paymentError } = await supabase
        .from('payments')
        .insert({
          user_id: user.id,
          transaction_ref: `setup_${type}_${Date.now()}`,
          amount: businessInfo.setupFee,
          currency: "USD",
          payment_type: "one_time",
          status: "pending",
          business_type: type,
          metadata: {
            setup_type: "business_setup",
            business_size: type,
            flutterwave_link: businessInfo.flutterwaveLink,
          },
        });

      if (paymentError) {
        console.error("Error creating payment record:", paymentError);
        toast.error("Failed to initialize payment. Please try again.");
        return;
      }

      // Redirect directly to Flutterwave payment link
      window.location.href = businessInfo.flutterwaveLink;
    } catch (error) {
      console.error("Error:", error);
      toast.error("Unable to process payment. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 mb-8">
      <h2 className="text-2xl font-bold text-center mb-2">Select Your Business Size</h2>
      <p className="text-center text-muted-foreground mb-6">
        Complete your one-time setup fee to unlock a 30-day free trial, then choose monthly plans
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {(Object.keys(businessTypeInfo) as BusinessType[]).map((type) => {
          const { title, icon: Icon, description, setupFee } = businessTypeInfo[type];
          return (
            <Card key={type} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex flex-col items-center text-center space-y-4">
                <Icon className="h-12 w-12 text-primary" />
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="text-muted-foreground text-sm">{description}</p>
                <div className="bg-primary/10 px-4 py-2 rounded-lg">
                  <span className="text-2xl font-bold text-primary">${setupFee.toLocaleString()}</span>
                  <p className="text-sm text-muted-foreground">One-time setup fee</p>
                  <p className="text-xs text-primary font-medium">Includes 30-day free trial</p>
                </div>
                <Button
                  onClick={() => handleSelectBusinessType(type)}
                  disabled={loading && selectedType === type}
                  className="w-full"
                  size="lg"
                >
                  {loading && selectedType === type ? "Processing..." : "Pay Setup Fee"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
